import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import '../css/cesExperience.css';

const services = [
  {
    id: 'psicologia',
    title: 'Psicología',
    lead: 'Acompañamiento terapéutico individual, pareja y familia.',
    bullets: ['Ansiedad, estrés y estado de ánimo', 'Terapia para adultos, adolescentes e infantil', 'Plan personalizado con seguimiento semanal'],
  },
  {
    id: 'fisioterapia',
    title: 'Fisioterapia',
    lead: 'Recupera movilidad, energía y bienestar corporal.',
    bullets: ['Dolor muscular y articular', 'Prevención y readaptación', 'Tratamiento integral en coordinación con psicología'],
  },
  {
    id: 'nutricion',
    title: 'Nutrición',
    lead: 'Mejora tu relación con la comida desde la salud y el equilibrio.',
    bullets: ['Educación nutricional realista', 'Plan adaptado a tu ritmo de vida', 'Hábitos sostenibles sin dietas extremas'],
  },
  {
    id: 'logopedia',
    title: 'Logopedia',
    lead: 'Intervención del lenguaje, comunicación y aprendizaje.',
    bullets: ['Dificultades de habla y lenguaje', 'Acompañamiento infantil y adulto', 'Trabajo coordinado con familia y centro'],
  },
];

const stats = [
  { label: 'Servicios integrados', value: '5+' },
  { label: 'Profesionales', value: '10+' },
  { label: 'Valoración media', value: '5.0★' },
  { label: 'Ubicación', value: 'Murcia' },
];

const testimonials = [
  {
    quote:
      'Gracias al enfoque conjunto de psicología y nutrición he notado cambios reales en pocas semanas. Me sentí acompañada desde el primer día.',
    author: 'Paciente CES',
  },
  {
    quote:
      'El centro transmite calma y profesionalidad. Todo está pensado para que te sientas segura y puedas avanzar a tu ritmo.',
    author: 'Familia CES',
  },
  {
    quote:
      'Reservar cita fue sencillo y el seguimiento impecable. Un equipo humano de verdad.',
    author: 'Usuario CES',
  },
];

const faqs = [
  {
    q: '¿Cómo sé qué servicio necesito?',
    a: 'En la primera sesión de valoración analizamos tu situación y te recomendamos el itinerario más adecuado, pudiendo combinar especialidades.',
  },
  {
    q: '¿Puedo pedir cita por WhatsApp?',
    a: 'Sí. Puedes escribir directamente al +34 614 22 43 42 o al +34 614 25 38 42 y te ayudamos con horarios y disponibilidad.',
  },
  {
    q: '¿Dónde estáis ubicadas?',
    a: 'En Plaza Federico García Lorca, Nº 14, Bajo. Alberca de las Torres, 30150 (Murcia).',
  },
];

export default function CesExperience() {
  const [activeService, setActiveService] = useState(services[0].id);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [openFaq, setOpenFaq] = useState(0);

  const selectedService = useMemo(
    () => services.find((service) => service.id === activeService) ?? services[0],
    [activeService],
  );

  const prevTestimonial = () => {
    setActiveTestimonial((current) =>
      current === 0 ? testimonials.length - 1 : current - 1,
    );
  };

  const nextTestimonial = () => {
    setActiveTestimonial((current) => (current + 1) % testimonials.length);
  };

  return (
    <>
      <Helmet>
        <title>CES · Propuesta web 2026</title>
        <meta
          name="description"
          content="Propuesta visual interactiva de nueva web CES: responsive, moderna y orientada a conversión."
        />
      </Helmet>

      <main className="ces-proposal">
        <section className="proposal-hero">
          <p className="eyebrow">Propuesta interactiva · CES</p>
          <h1>Calma tu mente. Escucha tu alma. Sana tu cuerpo.</h1>
          <p>
            Rediseño moderno de la web de CES con enfoque de conversión, estética premium
            y experiencia responsive en desktop, tablet y móvil.
          </p>
          <div className="hero-actions">
            <a href="#contacto-propuesta" className="btn btn-primary">Pedir cita</a>
            <a href="#servicios-propuesta" className="btn btn-secondary">Ver servicios</a>
          </div>
          <div className="stats-grid">
            {stats.map((item) => (
              <article key={item.label} className="stat-card">
                <strong>{item.value}</strong>
                <span>{item.label}</span>
              </article>
            ))}
          </div>
        </section>

        <section id="servicios-propuesta" className="proposal-section services-section">
          <header>
            <p className="eyebrow">Servicios CES</p>
            <h2>Un enfoque integral para tu bienestar</h2>
          </header>

          <div className="service-tabs" role="tablist" aria-label="Servicios CES">
            {services.map((service) => (
              <button
                key={service.id}
                role="tab"
                aria-selected={activeService === service.id}
                className={activeService === service.id ? 'tab active' : 'tab'}
                onClick={() => setActiveService(service.id)}
                type="button"
              >
                {service.title}
              </button>
            ))}
          </div>

          <article className="service-panel" role="tabpanel">
            <h3>{selectedService.title}</h3>
            <p>{selectedService.lead}</p>
            <ul>
              {selectedService.bullets.map((bullet) => (
                <li key={bullet}>{bullet}</li>
              ))}
            </ul>
          </article>
        </section>

        <section className="proposal-section testimonial-section">
          <header>
            <p className="eyebrow">Confianza real</p>
            <h2>Historias de cambio</h2>
          </header>

          <article className="testimonial-card">
            <p>“{testimonials[activeTestimonial].quote}”</p>
            <span>{testimonials[activeTestimonial].author}</span>
            <div className="testimonial-actions">
              <button type="button" onClick={prevTestimonial} aria-label="Testimonio anterior">←</button>
              <button type="button" onClick={nextTestimonial} aria-label="Siguiente testimonio">→</button>
            </div>
          </article>
        </section>

        <section className="proposal-section faq-section" id="contacto-propuesta">
          <header>
            <p className="eyebrow">Contacto</p>
            <h2>¿Lista para empezar?</h2>
          </header>

          <div className="contact-card">
            <p><strong>Dirección:</strong> Plaza Federico García Lorca, Nº 14, Bajo · Alberca de las Torres · Murcia.</p>
            <p><strong>Email:</strong> info@cespsicologiaybienestar.com</p>
            <p><strong>WhatsApp:</strong> +34 614 22 43 42 · +34 614 25 38 42</p>
            <a href="https://cespsicologiaybienestar.com/pide-tu-cita" className="btn btn-primary">Reservar cita</a>
          </div>

          <div className="faq-list">
            {faqs.map((faq, index) => {
              const isOpen = openFaq === index;
              return (
                <article key={faq.q} className={isOpen ? 'faq-item open' : 'faq-item'}>
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? -1 : index)}
                    aria-expanded={isOpen}
                  >
                    {faq.q}
                  </button>
                  {isOpen ? <p>{faq.a}</p> : null}
                </article>
              );
            })}
          </div>
        </section>
      </main>
    </>
  );
}
